// Slice B2.4-D — cross-session peek rollup (platform_dev only).

import { PEEK_TELEMETRY_WINDOW_DAYS } from "@/lib/activityDock/peekTelemetryTypes";
import {
  useActivityDockPeekCrossSessionStats,
  type CrossSessionPeekStatsDto,
} from "@/hooks/useActivityDockPeekCrossSessionStats";

type Props = {
  /** Parent section expanded — defer fetch until reviewer opens the panel. */
  enabled: boolean;
};

export function DevPanelActivityDockPeekCrossSessionSection({ enabled }: Props) {
  const { state, reload } = useActivityDockPeekCrossSessionStats(enabled);

  if (state.status === "hidden") return null;

  return (
    <div className="rounded-xl border border-indigo-200 bg-indigo-50/50 p-3 dark:border-indigo-900/60 dark:bg-indigo-950/30">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-indigo-800 dark:text-indigo-200">
            Cross-session tour priority
          </div>
          <p className="mt-0.5 text-[11px] text-indigo-700/90 dark:text-indigo-300/90">
            Last {PEEK_TELEMETRY_WINDOW_DAYS} days · platform_dev only · user id attribution
            only (no names/emails)
          </p>
        </div>
        <button
          type="button"
          onClick={reload}
          disabled={state.status === "loading"}
          className="rounded-lg border border-indigo-300 bg-white px-2 py-1 text-[11px] font-medium text-indigo-900 disabled:opacity-50 dark:border-indigo-700 dark:bg-indigo-950 dark:text-indigo-100"
        >
          Refresh
        </button>
      </div>

      {state.status === "loading" ? (
        <p className="text-xs text-indigo-700 dark:text-indigo-300">Loading cross-session rollup…</p>
      ) : null}

      {state.status === "error" ? (
        <p className="text-xs text-rose-700 dark:text-rose-300">{state.error}</p>
      ) : null}

      {state.status === "ready" ? (
        <CrossSessionBody summary={state.summary} degraded={state.degraded} />
      ) : null}
    </div>
  );
}

function CrossSessionBody(props: {
  summary: CrossSessionPeekStatsDto;
  degraded: boolean;
}) {
  const { summary, degraded } = props;

  if (summary.total === 0) {
    return (
      <p className="text-xs text-indigo-700 dark:text-indigo-300">
        No persisted peeks in the last {summary.windowDays} days.
        {degraded ? " (telemetry table may be missing in this env.)" : ""}
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {degraded ? (
        <p className="text-[11px] text-amber-800 dark:text-amber-200">
          Degraded read — table missing or query failed.
        </p>
      ) : null}
      <p className="text-xs text-indigo-800 dark:text-indigo-200">
        {summary.total} peek{summary.total === 1 ? "" : "s"} · {summary.uniqueUsers} user
        {summary.uniqueUsers === 1 ? "" : "s"} · {summary.uniqueOrgs} org
        {summary.uniqueOrgs === 1 ? "" : "s"}
      </p>

      <div className="grid gap-2 md:grid-cols-2">
        <MiniList title="Tour priority (sections)" entries={summary.topReasons.map((r) => ({
          key: r.reasonSectionId,
          label: r.reasonSectionId,
          count: r.count,
          hint: [r.topBucket, r.topMode].filter(Boolean).join(" · ") || undefined,
        }))} />
        <MiniList
          title="By bucket"
          entries={Object.entries(summary.perBucket).map(([key, count]) => ({
            key,
            label: key,
            count,
          }))}
        />
      </div>

      <MiniList
        title="(section, mode) hot spots"
        entries={Object.entries(summary.perReasonMode).flatMap(([reason, byMode]) =>
          Object.entries(byMode).map(([mode, count]) => ({
            key: `${reason}-${mode}`,
            label: `${reason} → ${mode}`,
            count,
          })),
        ).sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))}
      />
    </div>
  );
}

function MiniList(props: {
  title: string;
  entries: ReadonlyArray<{ key: string; label: string; count: number; hint?: string }>;
}) {
  if (props.entries.length === 0) {
    return (
      <div className="rounded-lg border border-indigo-200/80 bg-white/60 p-2 dark:border-indigo-800 dark:bg-indigo-950/40">
        <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-indigo-700 dark:text-indigo-300">
          {props.title}
        </div>
        <p className="text-[11px] text-indigo-600 dark:text-indigo-400">—</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-indigo-200/80 bg-white/60 p-2 dark:border-indigo-800 dark:bg-indigo-950/40">
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-indigo-700 dark:text-indigo-300">
        {props.title}
      </div>
      <ul className="max-h-40 space-y-1 overflow-y-auto">
        {props.entries.map((row) => (
          <li key={row.key} className="flex items-baseline justify-between gap-2 text-xs">
            <span className="min-w-0 font-mono text-indigo-900 dark:text-indigo-100">
              {row.label}
              {row.hint ? (
                <span className="ml-1 font-sans text-[10px] text-indigo-600 dark:text-indigo-400">
                  ({row.hint})
                </span>
              ) : null}
            </span>
            <span className="shrink-0 tabular-nums text-indigo-700 dark:text-indigo-300">
              {row.count}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
